// Commitment form page (create + edit) — FE-043.
//
// Single page wired into both routes:
//   /dashboard/commitments/new          → mode="create"
//   /dashboard/commitments/:id/edit     → mode="edit"
// Mirrors AccountFormPage's shape so the two surfaces feel
// consistent.

import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Link, useNavigate, useParams, useSearchParams,
} from "react-router-dom";

import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { CommitmentForm } from "./CommitmentForm";
import {
  blankCommitmentFormValues,
  serializeCommitmentForm,
  serializeCommitmentFormPatch,
  validateCommitmentForm,
  valuesFromDetail,
  type CommitmentFormErrors,
} from "./commitmentFormValidation";
import {
  useCommitmentDetailQuery,
  useCreateCommitmentMutation,
  useUpdateCommitmentMutation,
} from "./hooks";
import {
  CommitmentNotFoundError,
  type CommitmentFormMode,
  type CommitmentFormValues,
} from "./types";


export interface CommitmentFormPageProps {
  mode: CommitmentFormMode;
}


export function CommitmentFormPage({ mode }: CommitmentFormPageProps) {
  const navigate = useNavigate();
  const params = useParams();
  const [search] = useSearchParams();
  const commitmentId =
    mode === "edit" ? params.commitmentId ?? undefined : undefined;

  const detailQuery = useCommitmentDetailQuery(
    mode === "edit" ? commitmentId : undefined,
  );
  const createMutation = useCreateCommitmentMutation();
  const updateMutation = useUpdateCommitmentMutation();

  const initialValues = useMemo<CommitmentFormValues>(() => {
    if (mode === "edit" && detailQuery.data) {
      return valuesFromDetail(detailQuery.data);
    }
    const seed = blankCommitmentFormValues();
    if (mode === "create") {
      const fromUrl = search.get("account_id") ?? search.get("client_id");
      if (fromUrl) seed.account_id = fromUrl;
      const planYearFromUrl = search.get("plan_year_id");
      if (planYearFromUrl) seed.plan_year_id = planYearFromUrl;
    }
    return seed;
  }, [mode, detailQuery.data, search]);

  const [values, setValues] = useState<CommitmentFormValues>(initialValues);
  const [errors, setErrors] = useState<CommitmentFormErrors>({});

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const cancelHref = mode === "create"
    ? "/dashboard/commitment-tracker"
    : `/dashboard/commitments/${commitmentId ?? ""}`;
  const backLabel = mode === "create"
    ? "Back to Commitment Tracker"
    : "Back to Commitment";
  const heading = mode === "create" ? "New Commitment" : "Edit Commitment";
  const subtitle = mode === "create"
    ? "Create a Commitment tied to an Account, owner, due date, and workflow status."
    : "Update fields used across the Commitment Tracker and dashboard widgets.";

  function handleChange<K extends keyof CommitmentFormValues>(
    key: K, value: CommitmentFormValues[K],
  ): void {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function handleSubmit(): void {
    const { errors: validationErrors, ok } = validateCommitmentForm(values);
    if (!ok) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    if (mode === "create") {
      const payload = serializeCommitmentForm(values);
      createMutation.mutate(payload, {
        onSuccess: (data) => {
          navigate(`/dashboard/commitments/${data.commitment_id}`, {
            replace: true,
          });
        },
      });
    } else if (commitmentId) {
      const patch = serializeCommitmentFormPatch(values);
      updateMutation.mutate(
        { commitment_id: commitmentId, values: patch },
        {
          onSuccess: () => {
            navigate(`/dashboard/commitments/${commitmentId}`, {
              replace: true,
            });
          },
        },
      );
    }
  }

  // Edit-mode loading / not-found / error gates.
  if (mode === "edit") {
    if (detailQuery.isLoading) {
      return (
        <div data-testid="commitment-form-page" className="flex flex-col gap-5">
          <DashboardLoadingState variant="card" />
        </div>
      );
    }
    if (detailQuery.isError) {
      if (detailQuery.error instanceof CommitmentNotFoundError) {
        return (
          <div
            data-testid="commitment-detail-not-found"
            className="flex flex-col gap-5"
          >
            <DashboardEmptyState
              title="Commitment not found"
              description="It may have been deleted, or the URL is wrong."
              action={
                <Link
                  to="/dashboard/commitment-tracker"
                  className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
                >
                  Back to Commitment Tracker
                </Link>
              }
            />
          </div>
        );
      }
      return (
        <div data-testid="commitment-form-page" className="flex flex-col gap-5">
          <DashboardErrorState
            title="Couldn't load this Commitment"
            description="Refresh to retry, or check the network connection."
            retry={() => detailQuery.refetch()}
          />
        </div>
      );
    }
  }

  const mutation = mode === "create" ? createMutation : updateMutation;
  const mutationError = mutation.isError
    ? (mutation.error as Error)
    : null;

  return (
    <div data-testid="commitment-form-page" className="flex flex-col gap-5">
      <header className="flex flex-col gap-2">
        <Link
          to={cancelHref}
          data-testid="commitment-form-back"
          className="inline-flex w-fit items-center gap-1 text-xs font-medium text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft size={12} aria-hidden="true" />
          {backLabel}
        </Link>
        <h1 className="text-lg font-semibold tracking-tight text-text-primary">
          {heading}
        </h1>
        <p className="max-w-xl text-sm text-text-secondary">{subtitle}</p>
      </header>
      <CommitmentForm
        mode={mode}
        values={values}
        errors={errors}
        onChange={handleChange}
        onSubmit={handleSubmit}
        isPending={mutation.isPending}
        mutationError={mutationError}
        cancelHref={cancelHref}
      />
    </div>
  );
}
